import { Directive, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { QuadroSaudeService } from '../service/quadro-saude.service';
import { FuncaoService, GlobalService, Login, Exercicio, Relatorio } from 'eddydata-lib';

@Directive()
export class Quadro3Saude implements OnDestroy {
  protected funcaoService: FuncaoService;
  protected globalService: GlobalService;
  private login: Login = new Login();
  protected unsubscribe: Subject<void> = new Subject();

  constructor(
    protected quadroServico: QuadroSaudeService,
    protected mes: number,
    protected exercicio: Exercicio) {
    this.globalService = new GlobalService();
    this.funcaoService = new FuncaoService();
    this.login = GlobalService.obterSessaoLogin();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public async listar(orgaos: number[]) {
    return await this.quadroServico
      .obterQuadro3(this.mes, this.exercicio.id, orgaos).toPromise();
  }

  public montarRelatorio(orgaos: number[]) {
    this.quadroServico.obterQuadro3(this.mes, this.exercicio.id, orgaos)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(dados => {

        Relatorio.imprimirPersonalizado(
          'QUADRO 3 - DESPESAS ORÇAMENTÁRIAS - SAÚDE'
          , this.login.usuario.nome,
          this.login.orgao.nome, this.login.brasao,
          this.conteudo(dados),
          'landscape', 'QUADRO 3 - DESPESAS ORÇAMENTÁRIAS - SAÚDE',
          {
            linhas: {
              hLineWidth() {
                return 1;
              },
              vLineWidth() {
                return 1;
              },
              hLineColor() {
                return 'black';
              },
              paddingLeft() {
                return 3;
              },
              paddingRight() {
                return 3;
              }
            }
          });
      });
  }

  private conteudo(dados: any[]): {}[] {

    // monta o cabecalho
    const registros: {}[] = [
      [{
        text: `REFERÊNCIA: ` +
          this.globalService.obterDataBR().monthNames[this.mes - 1].toLocaleUpperCase() + '/' + this.exercicio.ano,
        alignment: 'center',
        fontSize: 11,
        margin: [5, 5, 5, 5],
        bold: true,
        colSpan: 10
      }, '', '', '', '', '', '', '', '', ''],
      [{
        text: 'FUNÇÃO EDUCAÇÃO',
        alignment: 'center',
        bold: true, fontSize: 6
      }, {
        text: 'SUBFUNÇÕES',
        alignment: 'center',
        bold: true, fontSize: 6
      }, {
        text: 'FONTE DE RECURSOS',
        alignment: 'center',
        bold: true, fontSize: 6
      }, {
        text: 'CÓDIGO DE APLICAÇÃO',
        alignment: 'center',
        bold: true, fontSize: 6
      }, {
        text: 'CLASSIFIC. ECONÔMICA',
        alignment: 'center',
        bold: true, fontSize: 6
      }, {
        text: 'DOTAÇÃO INICIAL',
        alignment: 'center',
        bold: true, fontSize: 6
      }, {
        text: 'DOTAÇÃO ATUALIZADA',
        alignment: 'center',
        bold: true, fontSize: 6
      }, {
        text: 'DESPESA EMPENHADA',
        alignment: 'center',
        bold: true, fontSize: 6
      }, {
        text: 'DESPESA LIQUIDADA',
        alignment: 'center',
        bold: true, fontSize: 6
      }, {
        text: 'DESPESA PAGA',
        alignment: 'center',
        bold: true, fontSize: 6
      }]
    ];

    // monta o agrupamento do relatório
    let i = 0;
    let total1 = 0;
    let total2 = 0;
    let total3 = 0;
    let total4 = 0;
    let total5 = 0;
    for (const dado of dados) {
      // FUNÇÕES DE GOVERNO --------------------------------------------------------------------------------
      const grupos = this.funcaoService.agrupar(dado, 'funcao',
        ['orcada', 'orcada_atualizada', 'empenhada', 'liquidada', 'paga']);
      i++;
      registros.push([
        {
          text: i === 1 ? 'TÍPICAS' : 'ATÍPICAS',
          alignment: 'center',
          bold: true,
          colSpan: 10
        }, '', '', '', '', '', '', '', '', '']);
      for (const grupo of grupos) {
        registros.push([
          {
            text: grupo.grupo, bold: true, fontSize: 7
          },
          {
            text: ''
          },
          {
            text: ''
          },
          {
            text: ''
          },
          {
            text: ''
          },
          {
            text: this.funcaoService.convertToBrNumber(grupo.totalizadores['orcada']),
            alignment: 'right', bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(grupo.totalizadores['orcada_atualizada']),
            alignment: 'right', bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(grupo.totalizadores['empenhada']),
            alignment: 'right', bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(grupo.totalizadores['liquidada']),
            alignment: 'right', bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(grupo.totalizadores['paga']),
            alignment: 'right', bold: true, fontSize: 7
          }
        ]);
        total1 += +grupo.totalizadores['orcada'];
        total2 += +grupo.totalizadores['orcada_atualizada'];
        total3 += +grupo.totalizadores['empenhada'];
        total4 += +grupo.totalizadores['liquidada'];
        total5 += +grupo.totalizadores['paga'];
        // SUB-FUNÇÕES DE GOVERNO --------------------------------------------------------------------------------
        const subfuncoes = this.funcaoService.agrupar(grupo.registros, 'subfuncao',
          ['orcada', 'orcada_atualizada', 'empenhada', 'liquidada', 'paga']);
        for (const subfuncao of subfuncoes) {
          registros.push([
            {
              text: ''
            },
            {
              text: subfuncao.grupo, bold: true, fontSize: 7
            },
            {
              text: ''
            },
            {
              text: ''
            },
            {
              text: ''
            },
            {
              text: this.funcaoService.convertToBrNumber(subfuncao.totalizadores['orcada']),
              alignment: 'right', bold: true, fontSize: 7
            },
            {
              text: this.funcaoService.convertToBrNumber(subfuncao.totalizadores['orcada_atualizada']),
              alignment: 'right', bold: true, fontSize: 7
            },
            {
              text: this.funcaoService.convertToBrNumber(subfuncao.totalizadores['empenhada']),
              alignment: 'right', bold: true, fontSize: 7
            },
            {
              text: this.funcaoService.convertToBrNumber(subfuncao.totalizadores['liquidada']),
              alignment: 'right', bold: true, fontSize: 7
            },
            {
              text: this.funcaoService.convertToBrNumber(subfuncao.totalizadores['paga']),
              alignment: 'right', bold: true, fontSize: 7
            }
          ]);
          // RECURSOS DE GOVERNO --------------------------------------------------------------------------------
          const recursos = this.funcaoService.agrupar(subfuncao.registros, 'recurso',
            ['orcada', 'orcada_atualizada', 'empenhada', 'liquidada', 'paga']);
          for (const recurso of recursos) {
            registros.push([
              {
                text: ''
              },
              {
                text: ''
              },
              {
                text: recurso.grupo, bold: true, fontSize: 7
              },
              {
                text: ''
              },
              {
                text: ''
              },
              {
                text: this.funcaoService.convertToBrNumber(recurso.totalizadores['orcada']),
                alignment: 'right', bold: true, fontSize: 7
              },
              {
                text: this.funcaoService.convertToBrNumber(recurso.totalizadores['orcada_atualizada']),
                alignment: 'right', bold: true, fontSize: 7
              },
              {
                text: this.funcaoService.convertToBrNumber(recurso.totalizadores['empenhada']),
                alignment: 'right', bold: true, fontSize: 7
              },
              {
                text: this.funcaoService.convertToBrNumber(recurso.totalizadores['liquidada']),
                alignment: 'right', bold: true, fontSize: 7
              },
              {
                text: this.funcaoService.convertToBrNumber(recurso.totalizadores['paga']),
                alignment: 'right', bold: true, fontSize: 7
              }
            ]);
            // ELEMENTO DE DESPESA --------------------------------------------------------------------------------
            const elementos = this.funcaoService.agrupar(recurso.registros, ['elemento', 'orcada', 'orcada_atualizada'],
              ['empenhada', 'liquidada', 'paga']);
            for (const elemento of elementos) {
              registros.push([
                {
                  text: ''
                },
                {
                  text: ''
                },
                {
                  text: ''
                },
                {
                  text: '0000'
                },
                {
                  text: elemento.grupo['elemento'], bold: true, fontSize: 7
                },
                {
                  text: this.funcaoService.convertToBrNumber(elemento.grupo['orcada']),
                  alignment: 'right', bold: true, fontSize: 7
                },
                {
                  text: this.funcaoService.convertToBrNumber(elemento.grupo['orcada_atualizada']),
                  alignment: 'right', bold: true, fontSize: 7
                },
                {
                  text: this.funcaoService.convertToBrNumber(elemento.totalizadores['empenhada']),
                  alignment: 'right', bold: true, fontSize: 7
                },
                {
                  text: this.funcaoService.convertToBrNumber(elemento.totalizadores['liquidada']),
                  alignment: 'right', bold: true, fontSize: 7
                },
                {
                  text: this.funcaoService.convertToBrNumber(elemento.totalizadores['paga']),
                  alignment: 'right', bold: true, fontSize: 7
                }
              ]);
              //  ITENS -------------------------------------------------------------------------------------------
              for (const item of elemento.registros) {
                if ((+item.empenhada + +item.liquidada + +item.paga) !== 0) {
                  registros.push([
                    {
                      text: ''
                    },
                    {
                      text: ''
                    },
                    {
                      text: ''
                    },
                    {
                      text: item.aplicacao, fontSize: 7
                    },
                    {
                      text: item.despesa, fontSize: 7
                    },
                    { text: '-', alignment: 'center' },
                    { text: '-', alignment: 'center' },
                    { text: this.funcaoService.convertToBrNumber(item.empenhada), alignment: 'right', fontSize: 7 },
                    { text: this.funcaoService.convertToBrNumber(item.liquidada), alignment: 'right', fontSize: 7 },
                    { text: this.funcaoService.convertToBrNumber(item.paga), alignment: 'right', fontSize: 7 },
                  ]);
                }
              }
            }
          }
        }


      }
    }

    return [{
      layout: 'linhas',
      table: {
        dontBreakRows: true,
        headerRows: 0,
        widths: ['*', '*', '*', '*', '*', 55, 55, 55, 55, 55],
        body: registros
      }
    }];
  }

}
